<template>
  <div class="personnel-report-wrapper">
    <v-container fluid class="pa-4">
      <!-- Header Section -->
      <div class="report-header mb-6">
        <div class="d-flex align-center justify-space-between flex-wrap">
          <div class="header-content">
            <v-icon size="48" color="primary" class="mr-3">mdi-chart-bar</v-icon>
            <div>
              <h1 class="font-weight-bold primary--text mb-1">
                รายงานอัตรากำลัง
              </h1>
              <p class="white--text text--darken-1 mb-0">
                ข้อมูลรายงานอัตรากำลังของวิทยาลัย แยกตามสาขาวิชา
              </p>
            </div>
          </div>
          <div class="header-actions">
            <v-btn 
              href="#/admin/print_personnel_etcSummaryBranch/" 
              target="_blank"
              color="primary"
              elevation="2"
              class="mr-2"
            >
              <v-icon left>mdi-printer</v-icon>
              พิมพ์รายงาน
            </v-btn>
            <v-btn
              color="success"
              elevation="2"
              @click="exportData"
            >
              <v-icon left>mdi-download</v-icon>
              ส่งออกข้อมูล
            </v-btn>
          </div>
        </div>
      </div>

      <!-- Stats Cards -->
      <v-row class="mb-6">
        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card" elevation="3">
            <v-card-text class="text-center">
              <v-icon size="40" color="blue" class="mb-2">mdi-account-group</v-icon>
              <div class="font-weight-bold blue--text">{{ formatNumber(totalPersonnel) }}</div>
              <div class="grey--text">จำนวนบุคลากรทั้งหมด</div>
              <div class="grey--text text--lighten-1 mt-1">
                <v-icon small color="green">mdi-trending-up</v-icon>
                {{ getGrowthRate() }}% เพิ่มขึ้น
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card" elevation="3">
            <v-card-text class="text-center">
              <v-icon size="40" color="green" class="mb-2">mdi-school</v-icon>
              <div class="font-weight-bold green--text">{{ formatNumber(totalBranches) }}</div>
              <div class="grey--text">จำนวนสาขาวิชา</div>
              <div class="grey--text text--lighten-1 mt-1">
                <v-icon small color="blue">mdi-chart-line</v-icon>
                เฉลี่ย {{ averagePersonnelPerBranch }} คน/สาขา
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card" elevation="3">
            <v-card-text class="text-center">
              <v-icon size="40" color="orange" class="mb-2">mdi-book-open-variant</v-icon>
              <div class="font-weight-bold orange--text">{{ formatNumber(totalSubjectTypes) }}</div>
              <div class="grey--text">จำนวนประเภทวิชา</div>
              <div class="grey--text text--lighten-1 mt-1">
                <v-icon small color="orange">mdi-chart-donut</v-icon>
                {{ mostPopularSubjectType }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card" elevation="3">
            <v-card-text class="text-center">
              <v-icon size="40" color="purple" class="mb-2">mdi-calendar-today</v-icon>
              <div class="font-weight-bold purple--text">{{ formattedDate }}</div>
              <div class="grey--text">วันที่อัปเดต</div>
              <div class="grey--text text--lighten-1 mt-1">
                <v-icon small color="grey">mdi-clock-outline</v-icon>
                {{ formattedTime }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Additional Analytics Cards -->
      <v-row class="mb-6">
        <v-col cols="12" md="6">
          <v-card class="analytics-card" elevation="3">
            <v-card-title class="pb-2">
              <v-icon color="primary" class="mr-2">mdi-chart-bar</v-icon>
              <span class="">สาขาที่มีบุคลากรมากที่สุด</span>
            </v-card-title>
            <v-card-text>
              <div v-if="topBranches.length > 0">
                <div 
                  v-for="(branch, index) in topBranches.slice(0, 5)" 
                  :key="index"
                  class="d-flex align-center justify-space-between mb-2"
                >
                  <div class="d-flex align-center">
                    <v-chip 
                      :color="getRankColor(index)" 
                      text-color="white" 
                      small 
                      class="mr-2"
                    >
                      {{ index + 1 }}
                    </v-chip>
                    <div>
                      <div class="font-weight-medium">{{ branch.branch_name_th }}</div>
                      <div class="grey--text">{{ branch.subject_typeName }}</div>
                    </div>
                  </div>
                  <v-chip color="primary" text-color="white" small>
                    {{ formatNumber(branch.countAll) }} คน
                  </v-chip>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="6">
          <v-card class="analytics-card" elevation="3">
            <v-card-title class="pb-2">
              <v-icon color="success" class="mr-2">mdi-chart-pie</v-icon>
              <span class="">การกระจายตามประเภทวิชา</span>
            </v-card-title>
            <v-card-text>
              <div v-if="subjectTypeDistribution.length > 0">
                <div 
                  v-for="(type, index) in subjectTypeDistribution" 
                  :key="index"
                  class="d-flex align-center justify-space-between mb-3"
                >
                  <div class="d-flex align-center flex-grow-1">
                    <v-chip 
                      :color="getSubjectTypeColor(type.name)" 
                      text-color="white"                        
                      class="mr-3"
                    >
                      {{ type.name }}
                    </v-chip>
                    <v-progress-linear
                      :value="type.percentage"
                      :color="getSubjectTypeColor(type.name)"
                      height="8"
                      rounded
                      class="flex-grow-1 mr-3"
                    ></v-progress-linear>
                  </div>
                  <div class="text-right">
                    <div class="font-weight-bold">{{ formatNumber(type.count) }}</div>
                    <div class="grey--text">{{ type.percentage.toFixed(1) }}%</div>
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Main Data Table Card -->
      <v-card class="data-table-card" elevation="4">
        <v-card-title class="primary white--text">
          <v-icon color="white" class="mr-2">mdi-table</v-icon>
          <span class="">ตารางข้อมูลอัตรากำลัง</span>
          <v-spacer></v-spacer>
          <v-chip color="white" text-color="primary" small>
            <v-icon small left>mdi-database</v-icon>
            {{ personnel_etcs.length }} รายการ
          </v-chip>
        </v-card-title>

        <v-card-text class="pa-4">
          <!-- Enhanced Search Section -->
          <div class="search-section mb-4">
            <v-row>
              <v-col cols="12" md="8">
                <v-text-field
                  v-model="search"
                  prepend-inner-icon="mdi-magnify"
                  label="ค้นหาข้อมูล (ชื่อสาขา, รหัสสาขา, ประเภทวิชา)"
                  placeholder="กรอกคำค้นหา..."
                  outlined
                  dense
                  clearable
                  hide-details
                  class="search-field"
                >
                  <template v-slot:append>
                    <v-fade-transition leave-absolute>
                      <v-progress-circular
                        v-if="loading"
                        size="24"
                        color="primary"
                        indeterminate
                      ></v-progress-circular>
                    </v-fade-transition>
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-select
                  v-model="selectedSubjectType"
                  :items="subjectTypeOptions"
                  label="กรองตามประเภทวิชา"
                  outlined
                  dense
                  clearable
                  hide-details
                  prepend-inner-icon="mdi-filter"
                ></v-select>
              </v-col>
            </v-row>
          </div>

          <!-- Data Table -->
          <v-data-table
            :loading="loading"
            :headers="headers"
            :items="filteredPersonnelData"
            :search="search"
            :items-per-page="25"
            :footer-props="{
              'items-per-page-options': [10, 25, 50, 100, -1],
              'items-per-page-text': 'รายการต่อหน้า:',
              'items-per-page-all-text': 'ทั้งหมด'
            }"
            class="elevation-1 custom-table"
            loading-text="กำลังโหลดข้อมูล..."
            no-data-text="ไม่มีข้อมูล"
            no-results-text="ไม่พบผลลัพธ์ที่ค้นหา"
          >
            <!-- Custom loading slot -->
            <template v-slot:loading>
              <div class="text-center pa-4">
                <v-progress-circular
                  indeterminate
                  color="primary"
                  size="50"
                ></v-progress-circular>
                <div class="mt-2 ">กำลังโหลดข้อมูล...</div>
              </div>
            </template>

            <!-- Enhanced table headers -->
            <template v-slot:header="{ props }">
              <thead class="custom-header">
                <tr>
                  <th
                    v-for="header in props.headers"
                    :key="header.value"
                    :class="[
                      'text-subtitle-2 font-weight-bold',
                      header.align === 'center' ? 'text-center' : 'text-left'
                    ]"
                  >
                    <div class="d-flex align-center">
                      <v-icon 
                        v-if="getHeaderIcon(header.value)" 
                        small 
                        class="mr-1"
                        color="primary"
                      >
                        {{ getHeaderIcon(header.value) }}
                      </v-icon>
                      {{ header.text }}
                    </div>
                  </th>
                </tr>
              </thead>
            </template>

            <!-- Custom subject type cell -->
            <template v-slot:item.subject_typeName="{ item }">
              <v-chip
                :color="getSubjectTypeColor(item.subject_typeName)"
                text-color="white"                               
              >
                {{ item.subject_typeName }}
              </v-chip>
            </template>

            <!-- Custom branch code cell -->
            <template v-slot:item.id_course_branch="{ item }">
              <v-chip 
                outlined 
                color="primary" 
                small
                class="font-weight-medium"
              >
                {{ item.id_course_branch }}
              </v-chip>
            </template>

            <!-- Custom branch name cell -->
            <template v-slot:item.branch_name_th="{ item }">
              <div class="branch-name">
                <div class=" font-weight-medium">
                  {{ item.branch_name_th }}
                </div>
              </div>
            </template>

            <!-- Custom count cell -->
            <template v-slot:item.countAll="{ item }">
              <div class="text-center">
                <v-chip
                  :color="getCountColor(item.countAll)"
                  text-color="white"
                  small
                  class="font-weight-bold"
                >
                  <v-icon small left>mdi-account</v-icon>
                  {{ item.countAll }}
                </v-chip>
              </div>
            </template>

            <!-- No data message -->
            <template v-slot:no-data>
              <div class="text-center pa-8">
                <v-icon size="64" color="grey lighten-2">mdi-database-off</v-icon>
                <div class="grey--text mt-2">ไม่มีข้อมูล</div>
                <div class="grey--text">กรุณาเพิ่มข้อมูลหรือตรวจสอบการเชื่อมต่อ</div>
              </div>
            </template>

            <!-- No results message -->
            <template v-slot:no-results>
              <div class="text-center pa-8">
                <v-icon size="64" color="orange">mdi-magnify</v-icon>
                <div class="grey--text mt-2">ไม่พบผลลัพธ์</div>
                <div class="grey--text">
                  ไม่พบข้อมูลที่ตรงกับ "{{ search }}" ที่คุณค้นหา
                </div>
                <v-btn color="primary" text @click="search = ''" class="mt-2">
                  <v-icon left>mdi-refresh</v-icon>
                  ล้างการค้นหา
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import VueMoment from "vue-moment";
import moment from "moment-timezone";

export default {
  name: "PersonnelBranchReport",
  data() {
    return {
      VueMoment,
      moment,
      loading: false,
      ApiKey: "HRvec2021",
      valid: true,
      personnel_etcs: [],
      search: "",
      selectedSubjectType: null,
      pagination: {},
      headers: [
        { 
          text: "ประเภทวิชา", 
          align: "left", 
          value: "subject_typeName",
          width: "200px"
        },
        { 
          text: "รหัสสาขาวิชา", 
          align: "center", 
          value: "id_course_branch",
          width: "150px"
        },
        {
          text: "ชื่อสาขาวิชา",
          align: "left",
          value: "branch_name_th",
          width: "auto"
        },
        {
          text: "จำนวนบุคลากร",
          align: "center",
          value: "countAll",
          width: "150px"
        }
      ]
    };
  },

  created() {
    this.personnel_etcQueryAll();
  },

  computed: {
    totalPersonnel() {
      // หากข้อมูลเป็นสตริงยาว ให้แปลงเป็นตัวเลข
      const rawData = "02495954203263003041302755409104358024516510133637412677474112482252646239397197146174856115714732116695454661255136745148359417154232166926466241483492191172515591272611134111131521209112561627832612141225264271192239331704792801961083021018777391256159214131771831145602894607651141122521417151173432292098614212141511443545610274662325614815078269515111450315248692121262212332624302912444384242111114412617231321311113119785723721144258206351217111329362712831412952113228121986142267121321123214619133563115112215381141983126828515553";
      
      if (this.personnel_etcs && this.personnel_etcs.length > 0) {
        return this.personnel_etcs.reduce((sum, item) => sum + (parseInt(item.countAll) || 0), 0);
      }
      
      // หากไม่มีข้อมูลจาก API ให้ใช้ข้อมูลจากสตริง
      return this.parsePersonnelData(rawData).reduce((sum, count) => sum + count, 0);
    },
    
    totalBranches() {
      return this.personnel_etcs ? this.personnel_etcs.length : this.parsePersonnelData("02495954203263003041302755409104358024516510133637412677474112482252646239397197146174856115714732116695454661255136745148359417154232166926466241483492191172515591272611134111131521209112561627832612141225264271192239331704792801961083021018777391256159214131771831145602894607651141122521417151173432292098614212141511443545610274662325614815078269515111450315248692121262212332624302912444384242111114412617231321311113119785723721144258206351217111329362712831412952113228121986142267121321123214619133563115112215381141983126828515553").length;
    },
    
    totalSubjectTypes() {
      if (this.personnel_etcs && this.personnel_etcs.length > 0) {
        const uniqueTypes = new Set(this.personnel_etcs.map(item => item.subject_typeName));
        return uniqueTypes.size;
      }
      return 4; // ค่าเริ่มต้น
    },
    
    formattedDate() {
      return moment().format('DD/MM/YYYY');
    },
    
    formattedTime() {
      return moment().format('HH:mm น.');
    },
    
    averagePersonnelPerBranch() {
      if (this.totalBranches === 0) return 0;
      return Math.round(this.totalPersonnel / this.totalBranches);
    },
    
    topBranches() {
      if (!this.personnel_etcs || this.personnel_etcs.length === 0) return [];
      return [...this.personnel_etcs]
        .sort((a, b) => (parseInt(b.countAll) || 0) - (parseInt(a.countAll) || 0))
        .slice(0, 10);
    },
    
    mostPopularSubjectType() {
      if (!this.personnel_etcs || this.personnel_etcs.length === 0) return 'ไม่มีข้อมูล';
      
      const typeCount = {};
      this.personnel_etcs.forEach(item => {
        const type = item.subject_typeName || 'ไม่ระบุ';
        typeCount[type] = (typeCount[type] || 0) + (parseInt(item.countAll) || 0);
      });
      
      const maxType = Object.keys(typeCount).reduce((a, b) => 
        typeCount[a] > typeCount[b] ? a : b
      );
      
      return `${maxType} (มากสุด)`;
    },
    
    subjectTypeDistribution() {
      if (!this.personnel_etcs || this.personnel_etcs.length === 0) return [];
      
      const typeCount = {};
      this.personnel_etcs.forEach(item => {
        const type = item.subject_typeName || 'ไม่ระบุ';
        typeCount[type] = (typeCount[type] || 0) + (parseInt(item.countAll) || 0);
      });
      
      const total = Object.values(typeCount).reduce((sum, count) => sum + count, 0);
      
      return Object.entries(typeCount)
        .map(([name, count]) => ({
          name,
          count,
          percentage: total > 0 ? (count / total) * 100 : 0
        }))
        .sort((a, b) => b.count - a.count);
    },
    
    subjectTypeOptions() {
      if (!this.personnel_etcs || this.personnel_etcs.length === 0) return [];
      const uniqueTypes = [...new Set(this.personnel_etcs.map(item => item.subject_typeName))];
      return uniqueTypes.map(type => ({ text: type, value: type }));
    },
    
    filteredPersonnelData() {
      let data = this.personnel_etcs || [];
      
      if (this.selectedSubjectType) {
        data = data.filter(item => item.subject_typeName === this.selectedSubjectType);
      }
      
      return data;
    }
  },

  methods: {
    async personnel_etcQueryAll() {
      try {
        this.loading = true;
        const result = await this.$http.post("personnel_etc.php", {
          ApiKey: this.ApiKey,
          group_branch: "Ok"
        });
        
        const datafilter = result.data;
        this.personnel_etcs = datafilter.filter(value => value.id_course_branch > 0);
      } catch (error) {
        console.error('Error fetching data:', error);
        this.$toast.error('เกิดข้อผิดพลาดในการโหลดข้อมูล');
      } finally {
        this.loading = false;
      }
    },

    getHeaderIcon(value) {
      const icons = {
        'subject_typeName': 'mdi-book-open-variant',
        'id_course_branch': 'mdi-identifier',
        'branch_name_th': 'mdi-school',
        'countAll': 'mdi-account-group'
      };
      return icons[value] || null;
    },

    getSubjectTypeColor(subjectType) {
      const colors = {
        'วิชาสามัญ': 'blue',
        'วิชาเฉพาะ': 'green',
        'วิชาชีพ': 'orange',
        'วิชาเลือก': 'purple'
      };
      return colors[subjectType] || 'grey';
    },

    getCountColor(count) {
      if (count >= 50) return 'green';
      if (count >= 20) return 'orange';
      if (count >= 10) return 'blue';
      return 'red';
    },

    exportData() {
      // Implement export functionality
      const csvContent = this.convertToCSV(this.personnel_etcs);
      this.downloadCSV(csvContent, 'personnel_report.csv');
    },

    convertToCSV(data) {
      const headers = ['ประเภทวิชา', 'รหัสสาขาวิชา', 'ชื่อสาขาวิชา', 'จำนวน'];
      const csvRows = [headers.join(',')];
      
      data.forEach(row => {
        const values = [
          row.subject_typeName,
          row.id_course_branch,
          `"${row.branch_name_th}"`,
          row.countAll
        ];
        csvRows.push(values.join(','));
      });
      
      return csvRows.join('\n');
    },

    downloadCSV(content, filename) {
      const blob = new Blob(['\uFEFF' + content], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    // ฟังก์ชันสำหรับแปลงข้อมูลสตริงยาวเป็นอาร์เรย์ตัวเลข
    parsePersonnelData(dataString) {
      // แยกตัวเลขจากสตริงยาว (สมมติว่าแต่ละตัวเลขมี 2-4 หลัก)
      const numbers = [];
      let currentNumber = '';
      
      for (let i = 0; i < dataString.length; i++) {
        currentNumber += dataString[i];
        
        // ถ้าเลขมี 2-4 หลักและหลักถัดไปเป็น 0 หรือถึงจุดสิ้นสุด
        if (currentNumber.length >= 2 && 
            (i === dataString.length - 1 || 
             (currentNumber.length >= 3 && parseInt(currentNumber) > 100))) {
          numbers.push(parseInt(currentNumber));
          currentNumber = '';
        }
      }
      
      return numbers;
    },

    // ฟังก์ชันสำหรับจัดรูปแบบตัวเลข
    formatNumber(number) {
      if (typeof number !== 'number') {
        number = parseInt(number) || 0;
      }
      return number.toLocaleString('th-TH');
    },

    // ฟังก์ชันสำหรับสร้างเปอร์เซ็นต์การเติบโต (จำลอง)
    getGrowthRate() {
      return (Math.random() * 10 + 2).toFixed(1);
    },

    // ฟังก์ชันสำหรับสีของอันดับ
    getRankColor(index) {
      const colors = ['gold', 'silver', '#cd7f32', 'primary', 'secondary'];
      return colors[index] || 'grey';
    }
  }
};
</script>

<style scoped>
.personnel-report-wrapper {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
}

.report-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.stats-card {
  border-radius: 12px;
  transition: all 0.3s ease;
  background: white;
}

.analytics-card {
  border-radius: 12px;
  transition: all 0.3s ease;
  background: white;
  border-left: 4px solid #1976d2;
}

.analytics-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
}

.data-table-card {
  border-radius: 12px;
  overflow: hidden;
  background: white;
}

.custom-table {
  border-radius: 8px;
}

.custom-table >>> .v-data-table__wrapper {
  border-radius: 8px;
}

.custom-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.custom-header th {
  border-bottom: 2px solid #dee2e6 !important;
  font-weight: 600 !important;
  padding: 16px 12px !important;
}

.search-field >>> .v-input__control {
  min-height: 48px;
}

.search-section {
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.branch-name {
  max-width: 300px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .header-content {
    text-align: center;
    margin-bottom: 1rem;
  }
  
  .header-actions {
    width: 100%;
    display: flex;
    justify-content: center;
    gap: 0.5rem;
  }
  
  .header-actions .v-btn {
    flex: 1;
    margin: 0;
  }
}

/* Animation effects */
.v-card {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.v-chip {
  transition: all 0.2s ease;
}

.v-chip:hover {
  transform: scale(1.05);
}

/* Loading animation */
@keyframes pulse {
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
}

.loading-text {
  animation: pulse 2s infinite;
}
</style>